//
// whatsApp 接口 Python 迁移层
//
// 已迁移的接口在此集中处理，通过 VUE_APP_WHATSAPP_USE_PYTHON 环境变量控制：
//   true  = 走 Python 后端
//   false = 回退到原有的 PHP 后端
// 迁移完成后，只需删除各函数的 fallback 分支即可。
//

import axios from "@/utils/axios";
import { usePyConfig, usePyRequest } from "./usePyConfig";
import qs from "qs";
const PY_ENABLED = process.env.VUE_APP_WHATSAPP_USE_PYTHON === "true";

/**
 * waba_id注册
 * @param {string} waba_id
 * @param {string} phone_number_id
 * @param {string} code 嵌入式注册返回的 code
 * @returns { Promise<{code:number;msg:string;data:any}> }
 */
export function registerWaba(waba_id, phone_number_id, code) {
  // 普通 Cloud API 注册需要手机号 ID；Business App 共存账户请使用 registerBusinessAppWaba。
  const data = {
    waba_id,
    phone_number_id,
    code,
    auth_mode: "api"
  };
  if (PY_ENABLED) {
    const config = usePyConfig("post", "api/pandagpt/v1/whatsApp/authorize", data);
    return axios(...Object.values(config));
  }
  return axios("POST", "api/pandagpt/v1/whatsApp/registerWaba", data);
}

/**
 * WhatsApp Business App 共存账户授权
 * @param {string} waba_id 共存账户 onboarding 返回的 WABA ID
 * @param {string} phone_number_id 共存账户 onboarding 返回的手机号 ID
 * @param {string} code 嵌入式注册返回的 code
 * @returns { Promise<{code:number;msg:string;data:any}> }
 */
export function registerBusinessAppWaba(waba_id, phone_number_id, code) {
  // 共存账户固定带 coexistence=1，后端据此跳过普通手机号注册并走共存授权。
  const data = {
    waba_id,
    phone_number_id,
    code,
    coexistence: 1
  };
  if (PY_ENABLED) {
    const config = usePyConfig("post", "api/pandagpt/v1/whatsApp/authorize", data);
    return axios(...Object.values(config));
  }
  // 共存授权必须始终进入 authorize，避免本地环境关闭 Python 开关时误走普通 API 注册。
  return axios("POST", "api/pandagpt/v1/whatsApp/authorize", data);
}

/**
 * 开启 WhatsApp Business App 共存账户同步授权。
 * @param {string} waba_id WABA 账户 ID，即 WhatsAppPage.page_id
 * @param {string} [phone_number_id] 电话号码编号，缺省时后端自动取该页首个号码
 * @param {Array<"smb_app_state_sync"|"history">} [sync_types] 同步类型：联系人和聊天历史
 * @returns { Promise<{code:number;msg:string;data:any}> }
 */
export function setBusinessAppSyncConsent(waba_id, phone_number_id, sync_types = ["smb_app_state_sync", "history"]) {
  // 共存账户 authorize 成功后再调用同步 consent，避免普通 API 授权链路误触发历史同步。
  const data = {
    waba_id,
    sync_types
  };
  if (phone_number_id) data.phone_number_id = phone_number_id;
  if (PY_ENABLED) {
    const config = usePyConfig("post", "api/pandagpt/v1/whatsApp/set_sync_consent", data);
    return axios(...Object.values(config));
  }
  return axios("POST", "api/pandagpt/v1/whatsApp/set_sync_consent", data);
}

/**
 * WhatsApp媒体授权列表
 * @param { {page_name?:string; page:number; page_num:number} } params
 * @returns {Promise<{code:number;msg:string;data:any}>}
 */
export function getWabaList(params) {
  if (PY_ENABLED) {
    const params_stringify = qs.stringify(params);
    const config = usePyConfig("GET", `api/pandagpt/v1/whatsApp/getWabaList`, params);
    return axios(...Object.values(config));
  }
  return axios("GET", "api/pandagpt/v1/whatsApp/getWabaList", params);
}

/**
 * 检索手机号
 * @param {string} waba_id
 * @param {string} phone_number_id
 * @returns {Promise<{code:number;msg:string;data:any}>}
 */
export function phoneRetrieve(waba_id, phone_number_id) {
  if (PY_ENABLED) {
    const config = usePyConfig("post", "api/pandagpt/v1/whatsApp/phoneRetrieve", {
      waba_id,
      phone_number_id
    });
    return axios(...Object.values(config));
  }
  return axios("POST", "api/pandagpt/v1/whatsApp/phoneRetrieve", {
    waba_id,
    phone_number_id
  });
}

/**
 * 获取whatsapp手机号列表
 * @param {{page_id:string;page:number;page_num:number}} params
 * @returns {Promise<{code:number;msg:string;data:any}>}
 */
export function getWabaPhoneList(params) {
  if (PY_ENABLED) {
    const config = usePyConfig("get", "api/pandagpt/v1/whatsApp/getWabaPhoneList", params);
    return axios(...Object.values(config));
  }
  return axios("GET", "api/pandagpt/v1/whatsApp/getWabaPhoneList", params);
}

/**
 * 初始化WhatsApp手机号列表
 * @param {string} page_id 主页ID
 * @returns {Promise<{code:number;msg:string;data:any}>}
 */
export function initializeWhatsAppPhoneList(page_id) {
  if (PY_ENABLED) {
    const config = usePyConfig("POST", "api/pandagpt/v1/whatsApp/getPhoneNumbers", {
      page_id
    });
    return axios(...Object.values(config));
  }
  return axios("POST", "api/pandagpt/v1/whatsApp/getPhoneNumbers", {
    page_id
  });
}

/**
 * 获取 whatsapp 模板
 * @param {string} page_id
 * @param {string} [template_id]
 * @returns {Promise<WhatsAppTemplate>}
 */
export function getWhatsAppTemplate(page_id, template_id) {
  if (PY_ENABLED) {
    const config = usePyConfig("get", "api/pandagpt/v1/whatsApp/getTemplate", {
      page_id,
      template_id
    });
    return axios(...Object.values(config));
  } else {
    return axios("GET", "api/pandagpt/v1/whatsApp/getTemplate", {
      page_id
    });
  }
}

/**
 * 同步 whatsapp 模板列表
 * @param page_id
 * @param [page_size]
 * @returns {Promise<{code:number;msg:string;data:any}>}
 */
export function getWhatsAppTemplateList(page_id, page_size = 999) {
  if (PY_ENABLED) {
    const config = usePyConfig("post", "api/pandagpt/v1/whatsApp/getTemplateList", {
      page_id,
      page_size
    });
    return axios(...Object.values(config));
  } else {
    return axios("POST", "api/pandagpt/v1/whatsApp/getTemplateList", {
      page_id
    });
  }
}

/**
 * 添加群发任务
 * @param {Record<string,any>} params
 * @returns {Promise<DefaultResponseType>}
 */
export function whatsAppSendTaskInsert(params) {
  if (PY_ENABLED) {
    const config = usePyConfig("POST", "api/pandagpt/v1/whatsAppSendTask/insert", params);
    return axios(...Object.values(config));
  } else {
    return axios("POST", "api/pandagpt/v1/whatsAppSendTask/insert", params);
  }
}

/**
 * 获取群发任务列表
 * @param {{status?:1|2|3;keyword?:string;user?:string;start_dt?:string;end_dt?:string;waba?:string;page?:number;page_num?:number}} params
 * @returns {Promise<DefaultResponseType>}
 */
export function whatsAppSendTaskList(params) {
  if (PY_ENABLED) {
    const config = usePyConfig("get", "api/pandagpt/v1/whatsAppSendTask/lists", params);
    return axios(...Object.values(config));
  } else {
    return axios("get", "api/pandagpt/v1/whatsAppSendTask/lists", params);
  }
}
export function whatsAppSendTaskSetEnd(params) {
  if (PY_ENABLED) {
    const config = usePyConfig("POST", "api/pandagpt/v1/whatsAppSendTask/setEnd", params);
    return axios(...Object.values(config));
  } else {
    return axios("POST", "api/pandagpt/v1/whatsAppSendTask/setEnd", params);
  }
}

/**
 * 计算预估花费
 * @param params
 * @returns {Promise<DefaultResponseType>}
 */
export function whatsAppSendTaskEstimate(params) {
  const config = usePyConfig("POST", "api/pandagpt/v1/whatsAppSendTask/estimate", params);
  return axios(...Object.values(config));
}

/**
 * 群发任务风控校验：请求参数与创建群发任务一致，预估金额前必须先通过校验。
 * @param {Record<string,any>} params
 * @returns {Promise<DefaultResponseType>}
 */
export function whatsAppSendTaskRiskCheck(params) {
  const config = usePyConfig("POST", "api/pandagpt/v1/whatsAppSendTask/riskCheck", params);
  return axios(...Object.values(config));
}

/**
 * 上传 whatsapp 媒体文件
 * @param {FormData} formData
 */
export function whatsAppUploadMedia(formData) {
  const config = usePyConfig("POST", "api/pandagpt/v1/whatsApp/uploadMedia", formData);
  return axios(...Object.values(config));
}

/**
 * 获取任务汇总数据
 * @param {string} id 任务ID
 * @returns {Promise<DefaultResponseType>}
 */
export function whatsAppSendTaskGetSummary(id) {
  const config = usePyConfig("GET", "api/pandagpt/v1/whatsAppSendTask/getSummary", {
    id
  });
  return axios(...Object.values(config));
}

/**
 * 任务详细数据
 * @param {Object} params
 * @returns {Promise<DefaultResponseType>}
 */
export function whatsAppSendTaskGetTaskDetails(params) {
  const config = usePyConfig("GET", "api/pandagpt/v1/whatsAppSendTask/getTaskDetails", params);
  return axios(...Object.values(config));
}

/**
 * 根据选中的tag列表筛选手机号
 * @param {{page_id:string;country_code:string;label_id:Array<{id:string;name:string}>}} params
 * @returns {Promise<{code:number;msg:string;data:any}>}
 */
export function whatsAppSendTaskOptionWhatsappUser(params) {
  const config = usePyConfig("POST", "api/pandagpt/v1/whatsAppSendTask/optionWhatsappUser", params);
  return axios(...Object.values(config));
}

/**
 * 提交公司验证
 * @param {FormData} formData
 * @returns {Promise<DefaultResponseType>}
 */
export function submitBusinessVerification(formData) {
  const config = usePyConfig("post", "api/pandagpt/v1/whatsApp/submitBusinessVerification", formData);
  return axios(...Object.values(config));
}

/**
 * 查询公司验证提交状态（PLBV）
 * @param page_id
 * @returns {Promise<DefaultResponseType>}
 */
export function getVerificationSubmissionStatus(page_id) {
  const config = usePyConfig("post", "api/pandagpt/v1/whatsApp/getVerificationSubmissionStatus", {
    page_id
  });
  return axios(...Object.values(config));
}

/**
 * 获取媒体平台的授权数量
 * @param params
 * @returns {Promise<DefaultResponseType>}
 */
export function getPlatformsSummary(params) {
  const config = usePyConfig("get", "api/pandagpt/v1/platform/platforms", params);
  return axios(...Object.values(config));
}

/**
 * 获取waba账号信息
 * @param page_id
 * @returns {Promise<DefaultResponseType>}
 */
export function getWabaInfo(page_id) {
  const config = usePyConfig("post", "api/pandagpt/v1/whatsApp/getWabaInfo", {
    page_id
  });
  return axios(...Object.values(config));
}

/**
 * 获取团队权限状态
 * @return {Promise<DefaultResponseType<{is_plbv:number; is_whatsapp:number; team_id:string}>>}
 */
export function getTeamFuncStatus() {
  return usePyRequest("GET", "api/pandagpt/v1/team/getTeamFuncStatus");
}

/**
 * 检查所选目标用户的地区是否支持最高出价
 * @param params 参数同创建群发任务
 * @return {Promise<DefaultResponseType<*>>}
 */
export function checkMaxPriceCountry(params) {
  return usePyRequest("POST", "api/pandagpt/v1/whatsAppSendTask/checkMaxPriceCountry", params);
}